import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { fmtDate, ACTION_STATUS } from "@/lib/format";
import { WorkflowTracker } from "@/components/WorkflowTracker";
import { RefreshNow } from "@/components/RefreshNow";
import { Users, UserCheck, UserX, UserMinus, CalendarClock, KeySquare, ArrowUpRight, Contact, Mailbox, Server, Wifi, WifiOff, CircleSlash, AlertTriangle, GitCompareArrows, ShieldCheck, FlaskConical, Loader2, RefreshCw, Sparkles, X, StopCircle } from "lucide-react";

// Admin, self-hosted only: banner when a new, license-covered version is available.
function UpdateBanner() {
  const { t } = useLang();
  const { isAdmin, license } = useAuth();
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!isAdmin || license?.saas) return;
    api.get("/v1/system/update-check")
      .then((r) => { setInfo(r.data); setDismissed(localStorage.getItem("upd_dismiss") === r.data.latestVersion); })
      .catch(() => {});
  }, [isAdmin, license]);
  if (!info || !info.updateAvailable || !info.canAutoUpdate || dismissed) return null;
  const v = info.latestVersion;
  const dismiss = () => { localStorage.setItem("upd_dismiss", v); setDismissed(true); };
  return (
    <div className="flex flex-wrap items-center gap-3 border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="dashboard-update-banner">
      <Sparkles size={18} className="shrink-0 text-emerald-600" />
      <span className="flex-1 text-sm font-medium text-emerald-900">{t("upd.bannerAvailable").replace("{v}", v)}</span>
      <Link to="/settings?tab=updates" data-testid="dashboard-update-view"
        className="inline-flex items-center gap-1.5 bg-emerald-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-emerald-700">
        {t("upd.bannerView")} <ArrowUpRight size={14} />
      </Link>
      <button onClick={dismiss} data-testid="dashboard-update-dismiss" className="p-1 text-emerald-700 hover:text-emerald-900" title={t("common.dismiss")}><X size={16} /></button>
    </div>
  );
}

// Admin-only: warn when local login is off and only a single SSO admin remains — losing access to
// that one account would lock everyone out of the console.
function LockoutGuardBanner() {
  const { t } = useLang();
  const { isAdmin, user, license } = useAuth();
  const [st, setSt] = useState(null);
  const dismissKey = user ? `lockout_guard_dismissed_${user.id}` : null;
  const [dismissed, setDismissed] = useState(() => (dismissKey ? localStorage.getItem(dismissKey) === "1" : false));
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/auth/sso/status").then((r) => setSt(r.data)).catch(() => {});
  }, [isAdmin]);
  if (!isAdmin || !st || dismissed) return null;
  if (!st.localLoginDisabled || (st.ssoAdminCount ?? 99) > 1) return null;
  // Pointless to warn "add a second admin" when the license caps admins at one.
  if ((license?.maxAdmins ?? 99) <= 1) return null;
  const dismiss = () => { if (dismissKey) localStorage.setItem(dismissKey, "1"); setDismissed(true); };
  return (
    <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="dashboard-lockout-guard">
      <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
      <div className="flex-1">
        <div className="font-bold">{t("dash.lockoutTitle")}</div>
        <div>{t("dash.lockoutBody")}</div>
      </div>
      <Link to="/settings?tab=security" data-testid="dashboard-lockout-link"
        className="inline-flex shrink-0 items-center gap-1.5 bg-amber-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-amber-700">
        {t("dash.lockoutCta")} <ArrowUpRight size={14} />
      </Link>
      <button onClick={dismiss} data-testid="dashboard-lockout-dismiss" aria-label="dismiss"
        className="shrink-0 rounded-sm p-1 text-amber-500 transition-colors hover:bg-amber-100 hover:text-amber-700">
        <X size={16} />
      </button>
    </div>
  );
}

function E2EHealthCard() {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);
  const wasRunning = useRef(false);
  const load = () => api.get("/e2e/health").then((r) => setD(r.data)).catch(() => setD({ enabled: false }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Poll live progress so a running job (started here or elsewhere) shows on the dashboard.
    const poll = async () => {
      const p = await api.get("/e2e/progress").then((r) => r.data).catch(() => null);
      if (p?.running) { setProg(p); wasRunning.current = true; }
      else { setProg(null); if (wasRunning.current) { wasRunning.current = false; setBusy(false); load(); } }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, []);
  if (!d || !d.enabled) return null;

  const last = d.last;
  const verdict = last?.verdict || "none";
  const map = {
    pass: ["text-emerald-700 bg-emerald-50 border-emerald-200", ShieldCheck, t("e2e.pass")],
    warn: ["text-amber-700 bg-amber-50 border-amber-200", AlertTriangle, t("e2e.warn")],
    fail: ["text-red-700 bg-red-50 border-red-200", AlertTriangle, t("e2e.fail")],
    error: ["text-zinc-600 bg-zinc-100 border-zinc-200", CircleSlash, t("e2e.error")],
    none: ["text-zinc-600 bg-zinc-100 border-zinc-200", FlaskConical, t("e2e.never")],
  };
  const [cls, Icon, lbl] = map[verdict] || map.none;
  const c = last?.counts || {};
  const total = (c.PASS || 0) + (c.FAIL || 0) + (c.WARN || 0) + (c.SKIP || 0);
  const barCls = { pass: "bg-emerald-400 hover:bg-emerald-500", warn: "bg-amber-400 hover:bg-amber-500", fail: "bg-red-400 hover:bg-red-500", error: "bg-zinc-300 hover:bg-zinc-400" };

  const running = busy || !!prog?.running;
  const doneN = prog?.completed || 0;
  const expN = prog?.expectedTotal || 0;
  const pct = expN > 0 ? Math.min(99, Math.round((doneN / expN) * 100)) : null;

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/e2e/run");
      toast.success(t("e2e.started"));
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const cancel = async () => {
    try { await api.post("/e2e/cancel"); toast.message(t("e2e.cancelledToast")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="dashboard-e2e-card" className="border border-zinc-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <Link to="/e2e-history" data-testid="dashboard-e2e-link" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900"><FlaskConical size={14} /> {t("e2e.title")}</Link>
        <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid="dashboard-e2e-verdict"><Icon size={11} /> {lbl}</span>
      </div>
      {running ? (
        <div className="mt-3" data-testid="dashboard-e2e-live">
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
            <span className="flex items-center gap-1.5 font-semibold text-primary" data-testid="dashboard-e2e-live-count">
              <Loader2 size={12} className="animate-spin" /> {t("e2e.running")} · {doneN}{expN > 0 ? ` / ~${expN}` : ""} {t("e2e.checksLabel")}
            </span>
            <span>{pct == null ? "" : `${pct}%`}</span>
          </div>
          {pct == null ? (
            <div className="h-2 w-full overflow-hidden rounded-sm bg-zinc-100" data-testid="dashboard-e2e-progress-indeterminate">
              <div className="h-full w-1/3 animate-[e2eslide_1.2s_ease-in-out_infinite] rounded-sm bg-primary" />
            </div>
          ) : (
            <div className="h-2 w-full overflow-hidden rounded-sm bg-zinc-100">
              <div className="h-full rounded-sm bg-primary transition-all duration-500" style={{ width: `${pct}%` }} data-testid="dashboard-e2e-progress-fill" />
            </div>
          )}
          {prog?.counts && (
            <div className="mt-1.5 text-[11px] text-zinc-500">
              <span className="font-bold text-emerald-600">{prog.counts.PASS || 0}</span> {t("e2e.pass")}
              {" · "}<span className={prog.counts.FAIL ? "font-bold text-red-600" : ""}>{prog.counts.FAIL || 0}</span> {t("e2e.fail")}
              {" · "}<span className={prog.counts.WARN ? "font-bold text-amber-600" : ""}>{prog.counts.WARN || 0}</span> {t("e2e.warn")}
            </div>
          )}
        </div>
      ) : last ? (
        <>
          <div className="mt-3 flex items-end gap-2">
            <span className="font-display text-4xl font-black tracking-tighter text-zinc-950" data-testid="dashboard-e2e-passcount">{c.PASS || 0}<span className="text-2xl text-zinc-300">/{total}</span></span>
            <span className="mb-1 text-xs text-zinc-400">{t("e2e.checksPassed")}</span>
          </div>
          <div className="mt-1 text-xs text-zinc-500" data-testid="dashboard-e2e-failwarn">
            <span className={c.FAIL ? "font-bold text-red-600" : ""}>{c.FAIL || 0} {t("e2e.failLabel")}</span>
            {" · "}
            <span className={c.WARN ? "font-bold text-amber-600" : ""}>{c.WARN || 0} {t("e2e.warnLabel")}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 font-mono text-[11px] text-zinc-400" data-testid="dashboard-e2e-lastrun">
            <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 font-bold uppercase tracking-wider text-zinc-500">{d.suite}</span>
            {fmtDate(last.ranAt)}
          </div>
          {last.error && (
            <Link to="/e2e-history" data-testid="dashboard-e2e-error" className="mt-2 block rounded-sm border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] leading-snug text-red-700 hover:bg-red-100">
              {last.error}
            </Link>
          )}
          {d.history.length > 1 && (
            <div className="mt-3 flex items-end gap-1" data-testid="dashboard-e2e-sparkline">
              {[...d.history].reverse().map((h, i) => {
                const hc = h.counts || {};
                const ht = (hc.PASS || 0) + (hc.FAIL || 0) + (hc.WARN || 0) + (hc.SKIP || 0);
                return <span key={i} title={`${hc.PASS || 0}/${ht} · ${fmtDate(h.ranAt)}`} className={`h-6 w-2.5 rounded-sm ${barCls[h.verdict] || barCls.error}`} data-testid="dashboard-e2e-bar" />;
              })}
            </div>
          )}
        </>
      ) : (
        <div className="mt-3 text-sm text-zinc-400" data-testid="dashboard-e2e-empty">{t("e2e.never")}</div>
      )}
      <button onClick={runNow} disabled={running} data-testid="dashboard-e2e-runnow" className="mt-4 inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50">
        {running ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {running ? t("e2e.running") : t("e2e.runNow")}
      </button>
      {running && (
        <button onClick={cancel} data-testid="dashboard-e2e-cancel" className="ml-2 mt-4 inline-flex items-center gap-1.5 border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50">
          <StopCircle size={13} /> {t("e2e.cancel")}
        </button>
      )}
    </div>
  );
}

function ReconcileHealthCard({ reconcile }) {
  const { t } = useLang();
  const [history, setHistory] = useState([]);
  useEffect(() => {
    api.get("/reconcile/reports?limit=12").then((r) => setHistory(r.data || [])).catch(() => {});
  }, []);
  const total = reconcile?.totalMismatches ?? 0;
  const status = reconcile?.noInventory ? "noInventory" : reconcile?.stale ? "stale" : total > 0 ? "drift" : "clean";
  const map = {
    clean: ["text-emerald-700 bg-emerald-50 border-emerald-200", ShieldCheck, t("reconcile.stClean")],
    drift: ["text-amber-700 bg-amber-50 border-amber-200", AlertTriangle, t("reconcile.stDrift")],
    stale: ["text-amber-700 bg-amber-50 border-amber-200", AlertTriangle, t("reconcile.stStale")],
    noInventory: ["text-zinc-600 bg-zinc-100 border-zinc-200", Server, t("reconcile.stNoInv")],
  };
  const [cls, Icon, lbl] = map[status];
  return (
    <div data-testid="dashboard-reconcile-card" className="border border-zinc-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <Link to="/settings?tab=sync" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900"><GitCompareArrows size={14} /> {t("dash.reconcileHealth")}</Link>
        <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-bold ${cls}`}><Icon size={11} /> {lbl}</span>
      </div>
      <Link to="/settings?tab=sync" className="mt-3 flex items-end gap-2">
        <span className="font-display text-4xl font-black tracking-tighter text-zinc-950">{total}</span>
        <span className="mb-1 text-xs text-zinc-400">{t("reconcile.mismatches")}</span>
      </Link>
      {history.length > 0 && (
        <div className="mt-3 flex items-end gap-1" data-testid="dashboard-reconcile-sparkline">
          {[...history].reverse().map((h) => {
            const n = (h.summary || {}).totalMismatches ?? 0;
            const bar = h.status === "clean" ? "bg-emerald-400 hover:bg-emerald-500" : h.status === "noInventory" ? "bg-zinc-300 hover:bg-zinc-400" : "bg-amber-400 hover:bg-amber-500";
            return <Link key={h.id} to={`/settings?tab=sync&report=${h.id}`} title={`${n} ${t("reconcile.mismatches")}`} className={`w-2.5 rounded-sm ${bar}`} style={{ height: `${Math.min(40, 4 + n * 5)}px` }} data-testid="dashboard-reconcile-bar" />;
          })}
        </div>
      )}
    </div>
  );
}

function AgentStatus({ operationMode }) {
  const { t } = useLang();
  const [a, setA] = useState(null);
  useEffect(() => {
    const load = () => api.get("/agent/status").then((r) => setA(r.data)).catch(() => setA({ error: true }));
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const cloudOnly = operationMode === "cloudOnly";
  const tokenSet = a && a.tokenSet;
  const connected = a && a.connected;
  const state = a?.error ? "error" : !tokenSet ? "unconfigured" : connected ? "online" : "offline";
  const META = {
    online: { cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", Icon: Wifi, label: t("dash.agentConnected") },
    offline: { cls: "bg-red-100 text-red-700", dot: "bg-red-500", Icon: WifiOff, label: t("dash.agentOffline") },
    unconfigured: { cls: "bg-zinc-100 text-zinc-500", dot: "bg-zinc-400", Icon: CircleSlash, label: t("dash.agentNotConfigured") },
    error: { cls: "bg-zinc-100 text-zinc-500", dot: "bg-zinc-400", Icon: CircleSlash, label: t("dash.agentNotConfigured") },
  }[state];
  const q = a?.queue || { queued: 0, claimed: 0, error: 0 };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="agent-status-widget">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-5 py-3">
        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600"><Server size={14} /> {t("dash.agentTitle")}</h2>
        <Link to="/settings?tab=agent" className="text-xs font-semibold text-primary hover:underline" data-testid="agent-status-settings">{t("dash.viewAll")}</Link>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            {state === "online" && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${META.dot} opacity-60`} />}
            <span className={`relative inline-flex h-3 w-3 rounded-full ${META.dot}`} />
          </span>
          <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${META.cls}`} data-testid="agent-status-badge">
            <META.Icon size={12} /> {META.label}
          </span>
        </div>

        {cloudOnly && <p className="mt-3 text-[11px] text-zinc-400">{t("dash.agentCloudOnly")}</p>}
        <div className="mt-3 space-y-1 text-xs text-zinc-500">
          <div className="flex justify-between"><span>{t("dash.agentLastSeen")}</span><span className="font-mono text-zinc-700" data-testid="agent-last-seen">{a?.lastSeen ? fmtDate(a.lastSeen) : t("dash.agentNever")}</span></div>
          <div className="flex justify-between"><span>{t("dash.agentLastResult")}</span><span className="font-mono text-zinc-700">{a?.lastResultAt ? fmtDate(a.lastResultAt) : t("dash.agentNever")}</span></div>
          {a?.lastAgentId ? <div className="flex justify-between"><span>{t("dash.agentId")}</span><span className="font-mono text-zinc-700">{a.lastAgentId}</span></div> : null}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-px bg-zinc-200" data-testid="agent-queue-depth">
          {[[t("dash.agentQueued"), q.queued, "text-zinc-900"], [t("dash.agentClaimed"), q.claimed, "text-blue-700"], [t("dash.agentErrors"), q.error, q.error > 0 ? "text-red-600" : "text-zinc-900"]].map(([lbl, val, cls]) => (
            <div key={lbl} className="bg-white p-2 text-center">
              <div className={`font-display text-2xl font-black ${cls}`}>{val ?? 0}</div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{lbl}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent, testid, to }) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className={`flex h-9 w-9 items-center justify-center ${accent}`}>
          <Icon size={18} strokeWidth={2.2} />
        </div>
        {to && <ArrowUpRight size={16} className="text-zinc-300 transition-colors group-hover:text-primary" />}
      </div>
      <div className="mt-4 font-display text-4xl font-black tracking-tighter text-zinc-950">{value}</div>
      <div className="mt-1 text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      {sub && <div className="mt-1 text-xs text-zinc-400">{sub}</div>}
    </>
  );
  if (to) {
    return <Link to={to} data-testid={testid} className="group block border border-zinc-200 bg-white p-5 transition-colors hover:bg-zinc-50">{inner}</Link>;
  }
  return <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>{inner}</div>;
}

// Admin banner: warns when any licensing group's SKU is at/below the low-seat alert threshold.
function LowSeatBanner() {
  const { t } = useLang();
  const [low, setLow] = useState([]);
  useEffect(() => {
    api.get("/group-license-usage").then((r) => {
      const items = [];
      (r.data || []).forEach((grp) => (grp.skus || []).forEach((sku) => {
        if (sku.belowThreshold) items.push({ ...sku, groupName: grp.groupName, groupId: grp.groupId });
      }));
      setLow(items);
    }).catch(() => {});
  }, []);
  if (low.length === 0) return null;
  return (
    <Link to="/licenses" data-testid="dashboard-lowseat-banner"
      className="flex items-start gap-3 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 transition-colors hover:bg-red-100">
      <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-600" />
      <div>
        <div className="font-bold">{t("dash.lowSeatTitle").replace("{n}", low.length)}</div>
        <div className="mt-0.5 space-y-0.5">
          {low.slice(0, 4).map((s) => (
            <div key={s.groupId + s.skuId} data-testid={`lowseat-item-${s.skuId}`}>
              {s.groupName} · {s.displayName}: <span className="font-semibold">{s.available <= 0 ? t("dash.lowSeatNone") : `${s.available} ${t("dash.seatFree")}`}</span>
            </div>
          ))}
          {low.length > 4 && <div className="text-xs text-red-600">+{low.length - 4}…</div>}
        </div>
      </div>
    </Link>
  );
}

// Seat usage per licensing group (group-based licensing) — spot a shortfall before onboarding.
function GroupLicenseUsage() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/group-license-usage").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid="dashboard-group-license-usage">
      <div className="flex items-center gap-2">
        <KeySquare size={14} className="text-primary" />
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">{t("dash.seatUsageTitle")}</h2>
      </div>
      <p className="mt-1 text-xs text-zinc-400">{t("dash.seatUsageHint")}</p>
      <div className="mt-4 space-y-4">
        {rows.map((grp) => (
          <div key={grp.groupId} data-testid={`seat-group-${grp.groupId}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-semibold text-zinc-800" title={grp.groupName}>{grp.groupName}</span>
              <span className="shrink-0 font-mono text-[10px] text-zinc-400">{grp.memberCount} {t("dash.seatMembers")}</span>
            </div>
            <div className="mt-2 space-y-2">
              {grp.skus.map((sku) => {
                const pct = sku.totalUnits ? Math.min(100, Math.round((sku.consumedUnits / sku.totalUnits) * 100)) : 0;
                const none = sku.available <= 0;
                const low = !none && sku.available <= 2;
                const bar = none ? "bg-red-500" : low ? "bg-amber-500" : "bg-primary";
                return (
                  <div key={sku.skuId} data-testid={`seat-sku-${sku.skuId}`}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-zinc-500" title={sku.displayName}>{sku.displayName}</span>
                      <span className={`shrink-0 font-mono font-semibold ${none ? "text-red-600" : low ? "text-amber-600" : "text-zinc-600"}`}
                        data-testid={`seat-available-${sku.skuId}`}>
                        {sku.known ? `${sku.available} ${t("dash.seatFree")}` : t("dash.seatUnknown")}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-sm bg-zinc-100">
                      <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] text-zinc-400">{sku.consumedUnits} / {sku.totalUnits} {t("dash.seats")}</div>
                    {none && <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-red-600" data-testid={`seat-warn-${sku.skuId}`}><AlertTriangle size={10} /> {t("dash.seatShortfall")}</div>}
                    {low && <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-amber-600" data-testid={`seat-low-${sku.skuId}`}><AlertTriangle size={10} /> {t("dash.seatLow")}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { t } = useLang();
  const { isAdmin, can } = useAuth();
  const [s, setS] = useState(null);

  const loadStats = () => api.get("/dashboard/stats").then((r) => setS(r.data));
  useEffect(() => {
    loadStats();
  }, []);

  if (!s)
    return (
      <>
        <PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")} />
        <div className="p-10 font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div>
      </>
    );

  const maxDept = Math.max(1, ...s.byDepartment.map((d) => d.value));

  return (
    <>
      <PageHeader title={t("dash.title")} subtitle={t("dash.subtitle")}>
        {can("onboarding") && (
        <Link
          to="/onboard"
          data-testid="dashboard-onboard-cta"
          className="flex h-10 items-center gap-2 bg-primary px-4 text-sm font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-[#002480]"
        >
          {t("dash.onboardCta")} <ArrowUpRight size={16} />
        </Link>
        )}
      </PageHeader>

      <div className="space-y-8 p-6 md:p-10">
        <UpdateBanner />
        <LockoutGuardBanner />
        {isAdmin && <LowSeatBanner />}
        {s.reconcile?.enabled && (s.reconcile.stale || s.reconcile.noInventory || (s.reconcile.totalMismatches > 0 && isAdmin)) && (
          <Link to="/settings?tab=sync" data-testid="dashboard-drift-warning"
            className="flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 transition-colors hover:bg-amber-100">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              {s.reconcile.noInventory ? (
                <span>{t("dash.driftNoInv")}</span>
              ) : s.reconcile.stale ? (
                <>
                  <div className="font-bold">{t("dash.driftStaleTitle")}</div>
                  <div>{t("dash.driftStaleBody").replace("{n}", s.reconcile.inventoryAgeMinutes ?? "?").replace("{m}", s.reconcile.maxAgeMinutes ?? "?")}</div>
                </>
              ) : (
                <span>{t("dash.driftDetected").replace("{n}", s.reconcile.totalMismatches ?? 0)}</span>
              )}
            </div>
          </Link>
        )}
        {can("workflows.view") && s.timedOutWorkflows > 0 && (
          <Link to="/actions?timedout=1" data-testid="dashboard-timedout-card"
            className="flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 transition-colors hover:bg-amber-100">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <div className="font-bold">{t("dash.timedOutTitle").replace("{n}", s.timedOutWorkflows)}</div>
              <div>{t("dash.timedOutBody")}</div>
            </div>
          </Link>
        )}
        <div className="-mb-4 flex justify-end" data-testid="dash-last-sync">
          <RefreshNow onDone={loadStats} />
        </div>
        <div className="grid grid-cols-2 gap-px bg-zinc-200 md:grid-cols-3 lg:grid-cols-7">
          <Metric icon={Users} label={t("dash.totalUsers")} value={s.totalUsers} accent="bg-primary/10 text-primary" testid="metric-total" to="/employees?filter=all" />
          <Metric icon={UserCheck} label={t("dash.active")} value={s.active} accent="bg-emerald-100 text-emerald-700" testid="metric-active" to="/employees?filter=active" />
          <Metric icon={UserX} label={t("dash.disabled")} value={s.disabled} accent="bg-zinc-100 text-zinc-500" testid="metric-disabled" to="/employees?filter=disabled" />
          <Metric icon={CalendarClock} label={t("dash.offboarding")} value={s.offboarding} accent="bg-amber-100 text-amber-700" testid="metric-offboarding" to="/employees?filter=offboarding" />
          <Metric icon={UserMinus} label={t("dash.leaved")} value={s.leaved ?? 0} accent="bg-violet-100 text-violet-700" testid="metric-leaved" to="/employees?filter=leaved" />
          <Metric icon={Contact} label={t("dash.guests")} value={s.guestUsers ?? 0} accent="bg-fuchsia-100 text-fuchsia-700" testid="metric-guests" to="/guests" />
          <Metric icon={Mailbox} label={t("dash.shared")} value={s.sharedMailboxes ?? 0} accent="bg-sky-100 text-sky-700" testid="metric-shared" to="/shared-mailboxes" />
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Recent actions */}
          <div className="border border-zinc-200 bg-white lg:col-span-2">
            <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-5 py-3">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">{t("dash.recentActions")}</h2>
              {can("workflows.view") && (
              <Link to="/actions" className="text-xs font-semibold text-primary hover:underline" data-testid="dashboard-view-actions">
                {t("dash.viewAll")}
              </Link>
              )}
            </div>
            <div className="divide-y divide-zinc-100">
              {s.recentActions.length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-zinc-400">{t("dash.noActions")}</div>
              )}
              {s.recentActions.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-zinc-900">{a.actionType}</div>
                    <div className="text-xs text-zinc-400">{fmtDate(a.createdAt)} · {a.requestedBy}</div>
                  </div>
                  <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACTION_STATUS[a.status] || ""}`}>
                    {t(`st.${a.status}`) === `st.${a.status}` ? a.status : t(`st.${a.status}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Side widgets */}
          <div className="space-y-8">
            {isAdmin && s.operationMode === "hybrid" && <AgentStatus operationMode={s.operationMode} />}
            {isAdmin && <GroupLicenseUsage />}
            {isAdmin && <E2EHealthCard />}
            {isAdmin && s.reconcile?.enabled && <ReconcileHealthCard reconcile={s.reconcile} />}
            <WorkflowTracker />

            <div className="border border-zinc-200 bg-white">
              <div className="border-b border-zinc-200 bg-amber-50 px-5 py-3">
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-800">{t("dash.scheduledOffboards")}</h2>
              </div>
              <div className="divide-y divide-zinc-100">
                {s.scheduledOffboards.length === 0 && (
                  <div className="px-5 py-6 text-center text-sm text-zinc-400">{t("dash.noneScheduled")}</div>
                )}
                {s.scheduledOffboards.map((o) => (
                  <Link to={`/employees/${o.id}`} key={o.id} className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50">
                    <span className="text-sm font-medium text-zinc-800">{o.displayName}</span>
                    <span className="font-mono text-xs text-amber-700">{o.leaveDate}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Department breakdown */}
        <div className="border border-zinc-200 bg-white p-5">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">{t("dash.headcount")}</h2>
          <div className="mt-5 space-y-3">
            {s.byDepartment.map((d) => (
              <div key={d.name} className="flex items-center gap-4">
                <div className="w-32 shrink-0 truncate text-sm font-medium text-zinc-700">{d.name}</div>
                <div className="h-5 flex-1 bg-zinc-100">
                  <div className="h-5 bg-primary/80" style={{ width: `${(d.value / maxDept) * 100}%` }} />
                </div>
                <div className="w-8 text-right font-mono text-sm text-zinc-500">{d.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
